import axios from 'axios';
import { message } from 'antd';

// 创建axios实例
const request = axios.create({
  // 同服务器部署时用相对路径 /api，与页面同源，无需跨域；本地开发在 .env.development 里配 REACT_APP_API_BASE_URL
  baseURL: process.env.REACT_APP_API_BASE_URL ?? '/api',
  timeout: 30000, // 增加超时时间，适配AI接口
});

/**
 * 获取媒体资源的完整 URL
 * 如果是相对路径（以 /static 开头），则拼接后端基础路径
 */
export const getMediaUrl = (url: string | null | undefined): string => {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) {
    return url;
  }
  
  // 获取后端基础路径
  const apiBase = process.env.REACT_APP_API_BASE_URL || '';
  let base = '';
  
  if (apiBase) {
    // 如果配置了 REACT_APP_API_BASE_URL，去掉末尾的 /api
    base = apiBase.replace(/\/api\/?$/, '');
  } else if (process.env.NODE_ENV === 'development') {
    // 本地开发环境下，如果没配环境变量，默认指向 3003 端口
    base = 'http://localhost:3003';
  } else {
    // 生产环境下，如果没配环境变量，默认使用当前域名
    base = window.location.origin;
  }
  
  // 确保 base 不以 / 结尾，url 以 / 开头
  const formattedBase = base.endsWith('/') ? base.slice(0, -1) : base;
  const formattedUrl = url.startsWith('/') ? url : `/${url}`;
  
  return `${formattedBase}${formattedUrl}`;
};

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    // 统一添加token到请求头（登录、注册、验证码登录、找回密码、发送验证码不需要token）
    const url = config.url || '';
    const isAuthEndpoint =
      url.includes('/users/login') ||
      url.includes('/users/register') ||
      url.includes('/users/login-by-code') ||
      url.includes('/users/password/reset') ||
      url.includes('/auth/send-code');
    if (!isAuthEndpoint) {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器 - 统一处理响应格式和错误
request.interceptors.response.use(
  async (response) => {
    // 如果响应是 Blob 类型，直接返回数据（不包装），因为它是原始媒体文件
    if (response.data instanceof Blob) {
      // 检查是否可能是错误信息（JSON 或 文本格式通常很小且不是 audio/mpeg）
      const blob = response.data;
      const isPotentialError = 
        blob.type.includes('json') || 
        blob.type.includes('text') || 
        (blob.type === '' && blob.size < 1000);

      if (isPotentialError) {
        try {
          const text = await blob.text();
          // 尝试解析为 JSON
          try {
            const json = JSON.parse(text);
            if (json.code !== undefined || json.error !== undefined || json.message !== undefined) {
              return json; // 如果是 JSON 错误格式，则按普通 JSON 处理
            }
          } catch (e) {
            // 不是 JSON，但如果是文本且包含错误关键字，也可能需要处理
            if (text.length < 500 && (text.includes('error') || text.includes('Unauthorized') || text.includes('Forbidden'))) {
              return { code: 500, message: text, error: true };
            }
          }
        } catch (e) {
          // 读取失败，继续返回 Blob
        }
      }
      return response.data as any;
    }
    
    // 统一返回响应数据
    // 如果后端返回格式为 { code, message, data }，则返回整个对象
    // 如果直接返回数据，则包装为 { code: 200, data: response.data }
    if (response.data && typeof response.data === 'object' && 'code' in response.data) {
      return response.data;
    }
    return { code: 200, message: 'success', data: response.data };
  },
  async (error) => {
    // 统一错误处理
    if (error.response) {
      let { status, data } = error.response;
      
      // 如果 data 是 Blob，尝试解析为 JSON 以获取错误信息
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          data = JSON.parse(text);
        } catch (e) {
          // 解析失败，保持原样
        }
      }

      const errorMessage = data?.message || data?.error || '请求失败';
      
      switch (status) {
        case 401:
          // 未授权/未登录
          // 登录和注册接口的401错误不应该清除token和跳转
          const url = error.config?.url || '';
          const isAuthEndpoint =
            url.includes('/users/login') ||
            url.includes('/users/register') ||
            url.includes('/users/login-by-code') ||
            url.includes('/users/password/reset') ||
            url.includes('/auth/send-code');
          
          if (!isAuthEndpoint) {
            message.error('未授权，请重新登录');
            // 清除token和用户信息
            localStorage.removeItem('token');
            localStorage.removeItem('auth_storage');
            // 跳转到登录页
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          } else {
            // 登录/注册接口的401错误，只显示错误信息，不清除token
            message.error(errorMessage || '认证失败');
          }
          break;
        case 403:
          // 拒绝访问
          message.error('拒绝访问，权限不足');
          break;
        case 404:
          // 资源未找到
          message.error('请求的资源不存在');
          break;
        case 429:
          // 限流
          message.error('请求过于频繁，请稍后再试');
          break;
        case 500:
          // 服务器错误
          message.error(`服务器错误: ${errorMessage}`);
          break;
        default:
          message.error(`请求失败 (${status}): ${errorMessage}`);
      }
    } else if (error.request) {
      // 请求已发出但没有收到响应
      message.error('网络连接异常，请检查网络');
    } else {
      // 其他错误
      message.error(`请求失败: ${error.message || '未知错误'}`);
    }
    return Promise.reject(error);
  }
);

export default request;
